import React from "react";
import { Lightning, TrendUp, TrendDown } from "@phosphor-icons/react";

export default function BriefingScreen({ challenge, onStart, onGridMode }) {
    const dur = challenge.duration || {};
    const playerCount = (challenge.players || []).length;
    return (
        <section className="border-2 border-fa-yellow/60 bg-[#0d0d0d] p-6 md:p-8 space-y-6" data-testid="briefing-screen">
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                    <Lightning size={11} weight="fill" />
                    Pre-game briefing
                </div>
                <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mt-2">
                    Read the market.<br />Decide before the deadline.
                </h2>
                <p className="font-mono text-[12px] text-white/70 mt-3 leading-relaxed">
                    You'll see <span className="text-fa-yellow font-bold">{playerCount} cards</span>, one at a time. For each player decide
                    <strong className="text-fa-mint"> BUY</strong>,
                    <strong className="text-fa-sky"> KEEP</strong>,
                    <strong className="text-[#F5A78D]"> AVOID</strong> or
                    <strong className="text-[#FF6B6B]"> SELL</strong>.
                    Outcome graded over the next <span className="text-fa-yellow font-bold">3 gameweeks (GW {dur.fromGameweek}–{dur.toGameweek})</span>.
                </p>
                <p className="font-mono text-[11px] text-white/50 mt-2 hidden md:block">
                    💡 On mobile: swipe <span className="text-fa-mint">right → BUY</span>, <span className="text-[#FF6B6B]">left → SELL</span>, <span className="text-fa-sky">up → KEEP</span>, <span className="text-[#F5A78D]">down → AVOID</span>.
                </p>
                <p className="font-mono text-[11px] text-white/50 mt-2 md:hidden">
                    💡 Swipe the card: <span className="text-fa-mint">→ BUY</span>, <span className="text-[#FF6B6B]">← SELL</span>, <span className="text-fa-sky">↑ KEEP</span>, <span className="text-[#F5A78D]">↓ AVOID</span>. Or tap a button.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-fa-mint/30 bg-fa-mint/5 p-4 space-y-2" data-testid="brief-buy-ins">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5">
                        <TrendUp size={11} weight="fill" /> Mass-buy players
                    </div>
                    <div className="font-mono text-[11px] text-white/70 leading-snug space-y-1">
                        <div>BUY with the crowd → <span className="text-fa-mint">+100</span> if STRONG_BUY (+30 bonus)</div>
                        <div>Already own → <span className="text-fa-yellow font-bold">KEEP +150</span></div>
                        <div>Resist hype → <span className="text-white/80">AVOID +130</span> if flops</div>
                        <div>Contrarian win → <span className="text-fa-yellow font-bold">SELL +175</span> if flops</div>
                    </div>
                </div>
                <div className="border border-[#F5A78D]/30 bg-[#F5A78D]/5 p-4 space-y-2" data-testid="brief-sell-offs">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5A78D] flex items-center gap-1.5">
                        <TrendDown size={11} weight="fill" /> Mass-sell players
                    </div>
                    <div className="font-mono text-[11px] text-white/70 leading-snug space-y-1">
                        <div>Contrarian dive → <span className="text-fa-yellow font-bold">BUY +175</span> if STRONG_BUY</div>
                        <div>Resist panic → <span className="text-fa-yellow font-bold">KEEP +150</span> if still solid</div>
                        <div>Go with crowd → <span className="text-white/80">SELL +130</span> if flops</div>
                        <div>Stay out → <span className="text-white/80">AVOID +120</span> if flops</div>
                    </div>
                </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-4 space-y-1.5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream">
                    Outcome thresholds (3-GW window)
                </div>
                <div className="font-mono text-[11px] text-white/60 leading-snug grid grid-cols-2 gap-y-1 gap-x-3">
                    <div><span className="text-fa-mint">STRONG_BUY</span> — 16+ pts, 200+ min</div>
                    <div><span className="text-fa-sky">GOOD_HOLD</span> — 10-15 pts, 180+ min</div>
                    <div><span className="text-[#F5A78D]">AVOID</span> — played but didn't deliver</div>
                    <div><span className="text-[#FF6B6B]">SELL</span> — &lt;7 pts, low min, or price drop</div>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    onClick={onStart}
                    className="bg-fa-yellow text-black font-display font-black uppercase tracking-wide text-base px-7 py-4 hover:bg-fa-yellow/90 transition-colors"
                    data-testid="briefing-start-btn"
                >
                    Start challenge →
                </button>
                <button
                    type="button"
                    onClick={onGridMode}
                    className="font-mono text-[11px] uppercase tracking-widest text-white/40 hover:text-white"
                    data-testid="briefing-grid-btn"
                >
                    Skip to grid view
                </button>
            </div>
        </section>
    );
}
